import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase'; // Updated to include db
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import '../styles/user.css';
import '../styles/auth.css';
import '../styles/buttons.css';


const passwordChecks = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'uppercase', label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'number', label: 'At least one number', test: (p) => /[0-9]/.test(p) },
];

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', className: '' };
  const score = passwordChecks.reduce((n, c) => n + (c.test(password) ? 1 : 0), 0);
  if (score <= 1) return { score, label: 'Weak', className: 'strength-weak' };
  if (score === 2) return { score, label: 'Medium', className: 'strength-medium' };
  return { score, label: 'Strong', className: 'strength-strong' };
}

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const strength = getPasswordStrength(password);
  const allRequirementsMet = passwordChecks.every((c) => c.test(password));

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!allRequirementsMet) {
      setError('Password does not meet the requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Save user to Firestore
      const uid = userCredential.user.uid;
      await setDoc(doc(db, 'users', uid), {
        email: userCredential.user.email,
        createdAt: new Date(),
      });
      // Write default activity preferences if they don't already exist
      const prefsRef = doc(db, 'users', uid, 'preferences', 'activities');
      const prefsSnap = await getDoc(prefsRef);
      if (!prefsSnap.exists()) {
        await setDoc(prefsRef, {
          activities: [
            'Walking', 'Running', 'Push Ups', 'Squats',
            'Jumping Jacks', 'Bicep Curls', 'Bench Press',
            'Sit Ups', 'Lunges', 'Plank'
          ]
        });
      }
      await sendEmailVerification(userCredential.user);
      navigate('/verify-email'); // Redirect to verify-email page
    } catch (error) {
      setError('Failed to sign up. Please try again.');
    }
  };

  return (
    <div className="App">
      <div className="signup-container">
        <h3>Sign Up</h3>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={handleSignup}>
          <label>
            Email:
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </label>
          <label>
            Password:
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </label>
          {password && (
            <div className="password-feedback">
              <ul className="password-requirements">
                {passwordChecks.map((c) => {
                  const passed = c.test(password);
                  return (
                    <li
                      key={c.key}
                      className={passed ? 'requirement-met' : 'requirement-unmet'}
                    >
                      <span className="requirement-icon">{passed ? '✓' : '✗'}</span>
                      {c.label}
                    </li>
                  );
                })}
              </ul>
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className={`strength-bar-fill ${strength.className}`}
                    style={{ width: `${(strength.score / passwordChecks.length) * 100}%` }}
                  />
                </div>
                <span className={`strength-label ${strength.className}`}>
                  {strength.label}
                </span>
              </div>
            </div>
          )}
          <label>
            Confirm Password:
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </label>
          {confirmPassword && confirmPassword !== password && (
            <p className="error-message">Passwords do not match.</p>
          )}
          <button type="submit" className="btn-primary" disabled={!allRequirementsMet || password !== confirmPassword}>
            Sign Up
          </button>
        </form>
        <p>
          Already have an account?{' '}
          <Link to="/login" className="login-link">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;
